<template>
    <div class="ttw">
        <!-- Progress hairline across the top edge of the modal -->
        <div class="ttw-rule" role="progressbar" :aria-valuenow="currentStep" aria-valuemin="1" :aria-valuemax="steps.length">
            <span class="ttw-rule-fill" :style="{ width: progressPct + '%' }"></span>
        </div>

        <button type="button" class="ttw-close" data-bs-dismiss="modal" aria-label="Close">
            <i class="fe fe-x"></i>
        </button>

        <div class="ttw-grid">
            <!-- Left rail: the stepper doubles as a running summary of the template -->
            <aside class="ttw-rail">
                <div class="ttw-brand">
                    <span class="ttw-eyebrow">aUT</span>
                    <h2 class="ttw-brand-title">New Tag Template</h2>
                </div>

                <ol class="ttw-steps">
                    <li
                        v-for="(name, index) in steps"
                        :key="name"
                        class="ttw-step"
                        :class="{
                            'is-done': index + 1 < currentStep,
                            'is-current': index + 1 === currentStep
                        }"
                    >
                        <button
                            type="button"
                            class="ttw-step-btn"
                            :disabled="index + 1 > maxVisitedStep"
                            @click="goToStep(index + 1)"
                        >
                            <span class="ttw-step-dot">
                                <i v-if="index + 1 < currentStep" class="fa fa-check"></i>
                                <span v-else>{{ index + 1 }}</span>
                            </span>
                            <span class="ttw-step-text">
                                <span class="ttw-step-name">{{ name }}</span>
                                <span class="ttw-step-value">{{ stepSummary[index] || "Not set" }}</span>
                            </span>
                        </button>
                    </li>
                </ol>
            </aside>

            <form id="wizardSteps" class="ttw-main" @submit.prevent="onAddTag()">
                <div class="ttw-scroll">
                    <header class="ttw-head">
                        <span class="ttw-eyebrow">
                            <span class="ttw-num">{{ currentStep }}</span>
                            <span class="ttw-sep">/</span>
                            <span class="ttw-num">{{ steps.length }}</span>
                            <span class="ttw-eyebrow-name">{{ steps[currentStep - 1] }}</span>
                        </span>
                        <h1 class="ttw-title">{{ stepCopy.title }}</h1>
                        <p v-if="stepCopy.blurb" class="ttw-blurb">{{ stepCopy.blurb }}</p>
                    </header>

                    <transition name="ttw-pane" mode="out-in">
                        <div :key="currentStep" class="ttw-pane">
                            <!-- Event Type -->
                            <template v-if="currentStep === 1">
                                <ChoiceList
                                    :options="eventTypeTiles"
                                    :selected="tagTemplate.event_type"
                                    :columns="2"
                                    compact
                                    @select="onSelectEventType"
                                ></ChoiceList>
                            </template>

                            <!-- Tag Type -->
                            <template v-else-if="currentStep === 2">
                                <section class="ttw-q">
                                    <div class="ttw-q-head">
                                        <span class="ttw-q-label">Structure</span>
                                        <span class="ttw-q-rule"></span>
                                    </div>
                                    <p class="ttw-q-text">Would you like to create an Atomic or a Container tag?</p>
                                    <ChoiceList
                                        :options="tagTypeTiles"
                                        :selected="tagType"
                                        @select="onSelectTagType"
                                    ></ChoiceList>
                                </section>

                                <div v-show="tagType == 'atomic'">
                                    <section class="ttw-q">
                                        <div class="ttw-q-head">
                                            <span class="ttw-q-label">Delivery</span>
                                            <span class="ttw-q-rule"></span>
                                        </div>
                                        <p class="ttw-q-text">Would you like to generate an image or a script tag?</p>
                                        <ChoiceList
                                            :options="atomicTagTypeTiles"
                                            :selected="atomicTagType"
                                            @select="onSelectAtomicTagType"
                                        ></ChoiceList>
                                    </section>

                                    <section v-show="atomicTagType == 'img'" class="ttw-q">
                                        <div class="ttw-q-head">
                                            <span class="ttw-q-label">Image format</span>
                                            <span class="ttw-q-rule"></span>
                                        </div>
                                        <p class="ttw-q-text">You've chosen an Image Atomic Tag Type, what format would you like to generate?</p>
                                        <ChoiceList
                                            :options="atomicImageTagFormatTiles"
                                            :selected="tagTemplate.tag_type"
                                            @select="onSelectTagFormat"
                                        ></ChoiceList>
                                    </section>

                                    <section v-show="atomicTagType == 'js'" class="ttw-q">
                                        <div class="ttw-q-head">
                                            <span class="ttw-q-label">Script format</span>
                                            <span class="ttw-q-rule"></span>
                                        </div>
                                        <p class="ttw-q-text">You've chosen a Script Atomic Tag Type, what format would you like to generate?</p>
                                        <ChoiceList
                                            :options="atomicScriptTagFormatTiles"
                                            :selected="tagTemplate.tag_type"
                                            @select="onSelectTagFormat"
                                        ></ChoiceList>
                                    </section>
                                </div>

                                <section v-show="tagType == 'container'" class="ttw-q">
                                    <div class="ttw-q-head">
                                        <span class="ttw-q-label">Container format</span>
                                        <span class="ttw-q-rule"></span>
                                    </div>
                                    <p class="ttw-q-text">You've chosen a Container Tag Type, what format would you like to generate?</p>
                                    <ChoiceList
                                        :options="containerTagFormatTiles"
                                        :selected="tagTemplate.tag_type"
                                        @select="onSelectTagFormat"
                                    ></ChoiceList>
                                </section>
                            </template>

                            <!-- Initiator: offered after Tag Type so the list can be
                                 narrowed to initiators that carry the chosen structure -->
                            <template v-else-if="currentStep === 3">
                                <InitiatorPicker
                                    :tiles="initiatorTiles"
                                    :selectedTile="tagTemplate.initiator"
                                    :emptyHint="initiatorEmptyHint"
                                    @onSelectTile="onSelectPlatform"
                                ></InitiatorPicker>
                            </template>

                            <!-- Logging -->
                            <template v-else-if="currentStep === 4">
                                <ChoiceList
                                    :options="loginModeTiles"
                                    :selected="enableLogginMode"
                                    @select="onLogginModeEnable"
                                ></ChoiceList>

                                <div v-if="enableLogginMode" class="ttw-log">
                                    <div class="ttw-q-head">
                                        <span class="ttw-q-label">Log detail</span>
                                        <span class="ttw-q-rule"></span>
                                    </div>

                                    <div class="ttw-seg-row">
                                        <span class="ttw-seg-label">Delivery</span>
                                        <div class="ttw-seg" role="group" aria-label="Log delivery">
                                            <button
                                                v-for="option in logDeliveryStreaming ? loginDeliveryTilesWithStreaming : loginDeliveryTiles"
                                                :key="option.value"
                                                type="button"
                                                class="ttw-seg-btn"
                                                :class="{ 'is-active': logDelivery === option.value }"
                                                @click="onSelectLogginDelivery(option.value)"
                                            >{{ option.title }}</button>
                                        </div>
                                    </div>

                                    <div class="ttw-seg-row">
                                        <span class="ttw-seg-label">Size</span>
                                        <div class="ttw-seg" role="group" aria-label="Log size">
                                            <button
                                                v-for="option in loginSizeTiles"
                                                :key="option.value"
                                                type="button"
                                                class="ttw-seg-btn"
                                                :class="{ 'is-active': logSize === option.value }"
                                                @click="onSelectLogginSize(option.value)"
                                            >{{ option.title }}</button>
                                        </div>
                                    </div>

                                    <div class="ttw-seg-row">
                                        <span class="ttw-seg-label">IP handling</span>
                                        <div class="ttw-seg" role="group" aria-label="Log IP type">
                                            <button
                                                v-for="option in loginIPTypeTiles"
                                                :key="option.value"
                                                type="button"
                                                class="ttw-seg-btn"
                                                :class="{ 'is-active': logIPType === option.value }"
                                                @click="onSelectLogginIPType(option.value)"
                                            >{{ option.title }}</button>
                                        </div>
                                    </div>

                                    <p class="ttw-mode">
                                        Resulting logging mode <code>{{ loggingMode }}</code>
                                    </p>
                                </div>
                            </template>

                            <!-- Name -->
                            <template v-else>
                                <div class="ttw-field">
                                    <label class="ttw-label" for="tagTemplateName">Template name</label>
                                    <input
                                        id="tagTemplateName"
                                        class="form-control"
                                        autocomplete="off"
                                        placeholder="e.g. Paid Search Conversion"
                                        v-model="tagTemplate.name"
                                    />
                                </div>

                                <div class="ttw-field">
                                    <label class="ttw-label">Category tags</label>
                                    <InputField
                                        v-model="tagTemplate.tag"
                                        :options="tagTemplate.tag"
                                        :showLabel="false"
                                        type="multiselect"
                                    />
                                    <p class="ttw-help">Tags group templates in the library. Type to create a new one.</p>
                                </div>

                                <div v-if="canPublish" class="ttw-publish">
                                    <div class="form-check">
                                        <input class="form-check-input" type="checkbox" v-model="tagTemplate.public" id="flexCheckDefault">
                                        <label class="form-check-label" for="flexCheckDefault">
                                            Make this tag template public
                                        </label>
                                    </div>
                                    <p class="ttw-help">Public templates are visible to every client under Browse Tag Templates.</p>
                                </div>
                            </template>
                        </div>
                    </transition>
                </div>

                <footer class="ttw-foot">
                    <div class="ttw-foot-left">
                        <button
                            v-if="currentStep > 1"
                            type="button"
                            class="btn btn-white ttw-btn"
                            @click="goToStep(currentStep - 1)"
                        >
                            <i class="fe fe-arrow-left"></i> Back
                        </button>
                        <button type="button" class="ttw-cancel" data-bs-dismiss="modal">Cancel</button>
                    </div>

                    <div class="ttw-foot-right">
                        <span v-if="blockedHint" class="ttw-hint">{{ blockedHint }}</span>
                        <button
                            v-if="currentStep < steps.length"
                            type="button"
                            class="btn ttw-btn"
                            :class="canContinue ? 'btn-primary' : 'btn-white'"
                            :disabled="!canContinue"
                            @click="goToStep(currentStep + 1)"
                        >
                            {{ continueLabel }} <i class="fe fe-arrow-right"></i>
                        </button>
                        <button
                            v-else
                            class="btn btn-primary ttw-btn"
                            type="submit"
                            :disabled="!tagTemplate.name || loading"
                        >
                            <span v-if="loading" class="spinner-border spinner-border-sm" role="status">
                                <span class="visually-hidden">Loading...</span>
                            </span>
                            <span v-else>Create Template</span>
                        </button>
                    </div>
                </footer>
            </form>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex'
import ChoiceList from '../aut/ChoiceList.vue';
import InitiatorPicker from '../aut/InitiatorPicker.vue';
import { isDuplicate } from '../../utils/commonFunction';
import { tag_event_types } from '../../utils/constOptionValues';
import InputField from "../InputField.vue";
const logKeys = {
                'b':'bbh',
                'c':'bbi',
                'd':'beh',
                'e':'bei',
                'f':'sbh',
                'g':'sbi',
                'h':'seh',
                'i':'sei',
                'j':'bbn',
                'k':'ben',
                'l':'sbn',
                'm':'sen',
            }

const stepCopy = [
    {
        title: "Begin by selecting an Event Type",
        blurb: "Event Type selection will incorporate both Paid and Owned media events. This will distinguish the type of data that is collected with your tag."
    },
    {
        title: "Select the Tag Type to generate",
        blurb: "Tag Type selection will distinguish the type of tag that is generated, relative to your event type."
    },
    {
        title: "Select the service that initiates data",
        blurb: "Platform selection will enable data syncing with the relevant DSP or Campaign Manager we support for tag generation. Only initiators that match the tag type you picked are offered. This step is optional."
    },
    {
        title: "Would you like to enable Logging Mode?",
        blurb: "Logs are kept for development and error checking. When enabled, you can tune how much detail each log line carries."
    },
    {
        title: "Name your Tag Template",
        blurb: "One last step. Give the template a name your team will recognize in the library."
    }
]

export default {
    components: {ChoiceList, InitiatorPicker, InputField},
    data() {
        return {
            steps: ["Event Type", "Tag Type", "Initiator", "Logging", "Name"],
            currentStep: 1,
            maxVisitedStep: 1,
            tagTemplate: {
                owner: this.$store.state.currentUser.email,
                cid: this.$store.state.currentClient.cid,
                name: null,
                event_type: null,
                initiator: null,
                tag_type: null,
                cookie: null,
                public: false,
                logging_mode: null
            },

            atomicTagTypeTiles: [
                    {title:"Javascript", subTitle:"Allows for extra collection of data on the page.", value:"js"},
                    {title:"Image", subTitle:"Raw logging pixel with query parameters.", value: "img"},
                    {title:"Redirect", subTitle:"URL to collect and then redirect to a certain URL.", value: "r"},
                ],

            atomicTagType: null,
            tagTypeTiles: [
                    {title:"Atomic", subTitle:"A pixel or endpoint with the purpose of firing once.", value:"atomic"},
                    {title:"Container", subTitle:"Allows the ability to fire additional tags.", value: "container"},
                ],
            tagType: null,

            loginModeTiles: [
                {title:"Yes, store logs", subTitle:"Store logs for development or error checking purposes", value:true},
                {title:"No, skip logging", subTitle:"Do not store additional logs.", value: false},
            ],
            loginDeliveryTiles: [
                { title: "Batch", value: "b" }
            ],
            loginDeliveryTilesWithStreaming: [
                { title: "Batch", value: "b" },
                { title: "Streaming", value: "s" }
            ],
            loginSizeTiles: [
                { title: "Base", value: "b" },
                { title: "Extended", value: "e" },
            ],
            loginIPTypeTiles: [
                { title: "Hashed IP", value: "h" },
                { title: "Plain IP", value: "i" },
                { title: "No IP", value: "n" },
            ],
            //Note: the cookie variant is c.gif, not a.gif - the "c" prefix means
            //cookie logic across every tag type (c.js, tc.dhj), and a.gif carries
            //no extra logic. TagTemplate.vue reads tag_type back the same way, so
            //swapping these two would show "Extra Logic: None" for a cookie tag.
            atomicImageTagFormatTiles: [
                { title:"Cookie  (Recommended)", subTitle:"Sets unique cookie if it is not present.", value:"c.gif"},
                { title:"NoCookie", subTitle:"No additional logic.", value: "a.gif"},
            ],
            atomicTagFormat: null,
            atomicScriptTagFormatTiles: [
                { title:"Cookie  (Recommended)", subTitle:"Sets unique cookie if it is not present.", value:"c.js"},
                { title:"Statistical", subTitle:"Additional Statistical ID logic to determine unique users.", value: "s.js"},
                { title:"Both", subTitle:"Cookie and Statistical ID logic combined.", value: "cs.js"},
            ],
            containerTagFormatTiles: [
                { title:"Cookie", subTitle:"Sets unique cookie if it is not present.", value:"tc.dhj"},
                { title:"Statistical", subTitle:"Additional Statistical ID logic to determine unique users.", value: "ts.dhj"},
                { title:"Both", subTitle:"Cookie and Statistical ID logic combined.", value: "tcs.dhj"},
                { title:"None", subTitle:"No additional logic.", value: "t.dhj"},
            ],
            loading: false,
            logDelivery: null,
            logSize: "",
            logIPType: "",
            editMode: true,
            enableLogginMode:null,
            tag: null
        }
    },

    computed: {
        //Note: every initiator, unfiltered. Used for the left-rail summary so a
        //selection keeps its label even if the tag type is changed afterwards
        allInitiatorTiles() {
            return (this.$store.state.aut.initiators || []).map((initiator) => {
                return {
                    title: initiator.name,
                    subTitle: initiator.description,
                    value: initiator.value,
                    iconSrc: initiator.logo,
                    tagType: initiator.tag_type || ""
                }
            });
        },
        //Note: the Tag Type step runs before the Initiator step, so the list only
        //offers initiators that carry the chosen structure. tag_type is optional on
        //an initiator, so unclassified ones stay visible for both structures rather
        //than vanishing until someone backfills them
        initiatorTiles() {
            if (!this.tagType) return this.allInitiatorTiles;
            return this.allInitiatorTiles.filter((tile) => !tile.tagType || tile.tagType == this.tagType);
        },
        initiatorEmptyHint() {
            if (this.tagType && this.allInitiatorTiles.length) {
                return `No initiators are set up for ${this.tagType} tags yet.`;
            }
            return null;
        },
        //Note: Tiles are derived from the shared event type list so the wizard stays in sync with it
        eventTypeTiles() {
            let tiles = tag_event_types.map((eventType) => {
                return { title: eventType.label, subTitle: eventType.description, value: eventType.value }
            });
            return tiles;
        },
        loggingMode() {
            let logMode = this.logDelivery+this.logSize+this.logIPType;
            for (const key in logKeys) {
                if(logKeys[key] == logMode)
                    return key
            }
            return 'a';
        },
        logDeliveryStreaming() {
            return (this.$store.state.aut.autClientData.config && this.$store.state.aut.autClientData.config.logDeliveryStreaming)?this.$store.state.aut.autClientData.config.logDeliveryStreaming: false;
        },
        canPublish() {
            return this.$store.getters.userRoles.isAqferAdmin || this.$store.getters.userRoles.isAqferManager;
        },
        stepCopy() {
            return stepCopy[this.currentStep - 1] || {};
        },
        progressPct() {
            return (this.currentStep / this.steps.length) * 100;
        },
        //Note: gating mirrors the original wizard - only Event Type and Tag Type
        //block the way forward, the Initiator and Logging steps are skippable.
        //Tag Type is step 2 now, Initiator step 3
        canContinue() {
            if (this.currentStep === 1) return this.tagTemplate.event_type !== null;
            if (this.currentStep === 2) return !!this.tagTemplate.tag_type;
            return true;
        },
        continueLabel() {
            if (this.currentStep === 3 && !this.tagTemplate.initiator) return "Skip";
            return "Continue";
        },
        blockedHint() {
            if (this.canContinue) return null;
            if (this.currentStep === 1) return "Select an event type to continue";
            return "Select a tag format to continue";
        },
        tagTypeSummary() {
            if (!this.tagTemplate.tag_type) return null;
            const formats = [
                ...this.atomicTagTypeTiles,
                ...this.atomicImageTagFormatTiles,
                ...this.atomicScriptTagFormatTiles,
                ...this.containerTagFormatTiles
            ];
            return [
                this.labelFor(this.tagTypeTiles, this.tagType),
                this.labelFor(formats, this.tagTemplate.tag_type)
            ].filter(Boolean).join(" · ").replace(/\s*\(Recommended\)/, "");
        },
        loggingSummary() {
            if (this.enableLogginMode === null) return null;
            if (!this.enableLogginMode) return "Disabled";
            const delivery = this.logDeliveryStreaming ? this.loginDeliveryTilesWithStreaming : this.loginDeliveryTiles;
            return [
                this.labelFor(delivery, this.logDelivery),
                this.labelFor(this.loginSizeTiles, this.logSize),
                this.labelFor(this.loginIPTypeTiles, this.logIPType)
            ].filter(Boolean).join(" · ");
        },
        //Note: one entry per step, shown under the step name in the left rail
        stepSummary() {
            return [
                this.labelFor(this.eventTypeTiles, this.tagTemplate.event_type),
                this.tagTypeSummary,
                this.labelFor(this.allInitiatorTiles, this.tagTemplate.initiator) || (this.maxVisitedStep > 3 ? "Skipped" : null),
                this.loggingSummary,
                this.tagTemplate.name
            ];
        }
    },

    watch: {
        loggingMode(value, oldValue) {
            this.tagTemplate.logging_mode = value;
        }
    },

    beforeMount(){
        let self=this;
        this.$store.dispatch('aut/fetchAutClientData', this.$store.state.currentClient.cid);
        this.$store.dispatch("aut/fetchInitiators");
        },
    methods: {
        ...mapActions('aut', {
            createTagTemplate: 'createTagTemplate',
            fetchTagTemplates: 'fetchTagTemplates'
        }),
        //Note: steps are driven by currentStep instead of Bootstrap tabs, so the
        //rail, progress bar and panes cannot drift out of sync
        goToStep(step) {
            if (step < 1 || step > this.steps.length) return;
            if (step > this.currentStep && !this.canContinue) return;
            if (step > this.maxVisitedStep) this.maxVisitedStep = step;
            this.currentStep = step;
        },
        labelFor(tiles, value) {
            if (value === null || value === undefined || value === "") return null;
            const match = (tiles || []).find((tile) => tile.value == value);
            return match ? match.title : null;
        },
        async onAddTag() {
            this.loading = true;
            let toastoptions;
            let response;
            let valid = false;
            try {
                valid = !isDuplicate(this.tagTemplate.name,this.$store.state.aut.tagTemplates, "name", null)
                if(valid){
                    this.tagTemplate.tag= this.tagTemplate.tag ?this.tagTemplate.tag.toString():"";
                    response = await this.createTagTemplate(this.tagTemplate)
                    toastoptions = {
                        message: "Tag added successfully.",
                        type: "success"
                    }
                }else{
                    toastoptions = {
                        message: "Already '"+this.tagTemplate.name+"' name exits.'",
                        type: "error",
                    };
                }
            } catch (error) {
                // if (error.response.data.error)
                //     this.error = error.response.data.error
                // else
                //     this.error = error.message

                toastoptions = {
                    message: "Something went wrong when adding the tag. Please contact support.",
                    type: "error"
                }
                return
            }
            //Dont hide and redirect page when name is duplicate
            if (valid){
                this.$store.dispatch('hideModal')
                if(response.data.id)
                    this.$router.push({ name: 'Tag Template', params: {id: response.data.id}} );
                else
                    this.$store.dispatch('aut/fetchTagTemplates')
            }
            this.loading = false;
            this.$store.dispatch('addToast', toastoptions);
        },

        onSelectEventType(val) {
            this.tagTemplate.event_type = val;
            if(val == "clk") {
                this.onSelectTagType("atomic");
                this.onSelectAtomicTagType("r");
            }
        },
        onSelectPlatform(val) {
            this.tagTemplate.initiator = val;
        },
        onSelectTagType(val) {
            this.tagType = val;
            //Note: an initiator picked for the other structure cannot carry over,
            //otherwise the template would keep an initiator the list no longer offers
            if (this.tagTemplate.initiator
                && !this.initiatorTiles.some((tile) => tile.value == this.tagTemplate.initiator)) {
                this.tagTemplate.initiator = null;
            }
        },
        onSelectTagFormat(val) {
            this.tagTemplate.tag_type = val;
        },

        onSelectAtomicTagType(val) {
           this.atomicTagType = val;
           if(this.atomicTagType == "r") {
                this.tagTemplate.tag_type = "r";
           }
        },
        onSelectAtomicTagFormat(val) {
            this.atomicTagFormat = val;
        },
        onLogginModeEnable(val) {
            this.enableLogginMode = val;
            if(!this.enableLogginMode){
                this.tagTemplate.logging_mode = "a";
                this.logDelivery = null;
                this.logSize = null;
                this.logIPType = null;
            }
            else{
                this.logDelivery = "b";
                this.logSize = "e";
                this.logIPType = "h";
            }

        },
        onSelectLogginDelivery(val) {
            this.logDelivery = val;

        },
        onSelectLogginSize(val) {
            this.logSize = val;

        },
        onSelectLogginIPType(val) {
            this.logIPType = val;

        }
    }

}
</script>

<style lang="scss">
@use '../../theme/scss/_user-variables.scss' as *;

.ttw {
    --ttw-ink: #12263F;
    --ttw-ink-soft: #6E84A3;
    --ttw-ink-faint: #95AAC9;
    --ttw-line: #E3EBF6;
    --ttw-surface: #ffffff;
    --ttw-rail-top: #F9FBFD;
    --ttw-rail-bottom: #EDF2F9;
    --ttw-accent: #2C7BE5;
    --ttw-accent-ink: #1657B0;
    --ttw-accent-wash: rgba(44, 123, 229, 0.08);
    --ttw-track: #EDF2F9;
    --ttw-mono: "SFMono-Regular", Menlo, Consolas, "Liberation Mono", monospace;

    position: relative;
    border-radius: inherit;
    overflow: hidden;
    background-color: var(--ttw-surface);
    color: var(--ttw-ink);

    .ttw-rule {
        height: 2px;
        background-color: var(--ttw-track);
    }

    .ttw-rule-fill {
        display: block;
        height: 100%;
        background-image: linear-gradient(90deg, var(--ttw-accent), #5b9bf0);
        transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .ttw-close {
        position: absolute;
        top: 0.75rem;
        right: 0.75rem;
        z-index: 5;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 1.75rem;
        height: 1.75rem;
        padding: 0;
        font-size: 1rem;
        line-height: 1;
        color: var(--ttw-ink-faint);
        background: none;
        border: 0;
        border-radius: 50%;
        transition: color 0.15s ease, background-color 0.15s ease;

        &:hover {
            color: var(--ttw-ink);
            background-color: var(--ttw-track);
        }
    }

    /* minmax(0, 1fr) on the row is what keeps the row from growing past the
       modal: without it a tall step pushes the sticky footer out of view
       instead of letting the content column scroll.
       The height is viewport-relative and leaves room for the dialog's own
       margins, so the modal always fits and stays optically centred - including
       on short laptop and landscape-phone viewports. */
    /* One height for every step, rather than a box that resizes as the content
       changes: the frame stays put while stepping, and the dialog's own margins
       are accounted for so it never overflows a short viewport. */
    .ttw-grid {
        display: grid;
        grid-template-columns: 15.5rem minmax(0, 1fr);
        grid-template-rows: minmax(0, 1fr);
        height: min(82vh, 46rem);
        overflow: hidden;
    }

    /* dvh tracks the shrinking URL bar on mobile browsers where vh does not */
    @supports (height: 1dvh) {
        .ttw-grid {
            height: min(82dvh, 46rem);
        }
    }

    /* ---------- Left rail ---------- */

    .ttw-rail {
        display: flex;
        flex-direction: column;
        padding: 1.5rem 1.125rem;
        overflow-y: auto;
        background-image: linear-gradient(170deg, var(--ttw-rail-top), var(--ttw-rail-bottom));
        border-right: 1px solid var(--ttw-line);
    }

    .ttw-eyebrow {
        display: inline-flex;
        align-items: baseline;
        gap: 0.375rem;
        font-size: 0.6875rem;
        font-weight: 600;
        letter-spacing: 0.1em;
        text-transform: uppercase;
        color: var(--ttw-ink-faint);
    }

    .ttw-brand-title {
        margin: 0.375rem 0 0;
        font-size: 1.0625rem;
        font-weight: 600;
        letter-spacing: -0.01em;
        color: var(--ttw-ink);
    }

    .ttw-steps {
        list-style: none;
        margin: 1.5rem 0 0;
        padding: 0;
        counter-reset: none;
    }

    .ttw-step {
        position: relative;

        /* Connector between the step dots */
        &:not(:last-child)::before {
            content: "";
            position: absolute;
            left: 1.1875rem;
            top: 2.125rem;
            bottom: 0.1875rem;
            width: 1px;
            background-color: #D2DDEC;
        }

        &.is-done::before {
            background-color: rgba(44, 123, 229, 0.4);
        }
    }

    .ttw-step-btn {
        display: flex;
        align-items: flex-start;
        gap: 0.625rem;
        width: 100%;
        padding: 0.4375rem 0.5rem;
        text-align: left;
        background: none;
        border: 0;
        border-radius: 0.4375rem;
        transition: background-color 0.15s ease;

        &:not(:disabled):hover {
            background-color: rgba(44, 123, 229, 0.07);
        }

        &:disabled {
            cursor: default;
        }
    }

    .ttw-step-dot {
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 auto;
        width: 1.5rem;
        height: 1.5rem;
        font-family: var(--ttw-mono);
        font-size: 0.6875rem;
        font-weight: 600;
        color: var(--ttw-ink-faint);
        background-color: var(--ttw-surface);
        border: 1px solid var(--ttw-line);
        border-radius: 50%;
        transition: all 0.18s ease;
    }

    .ttw-step.is-done .ttw-step-dot {
        font-size: 0.5625rem;
        color: var(--ttw-accent);
        background-color: var(--ttw-accent-wash);
        border-color: rgba(44, 123, 229, 0.35);
    }

    .ttw-step.is-current .ttw-step-dot {
        color: #ffffff;
        background-color: var(--ttw-accent);
        border-color: var(--ttw-accent);
        box-shadow: 0 0 0 3px rgba(44, 123, 229, 0.16);
    }

    .ttw-step-text {
        display: flex;
        flex-direction: column;
        gap: 0.0625rem;
        min-width: 0;
        padding-top: 0.0625rem;
    }

    .ttw-step-name {
        font-size: 0.8125rem;
        font-weight: 600;
        line-height: 1.35;
        color: var(--ttw-ink-soft);
    }

    .ttw-step.is-current .ttw-step-name,
    .ttw-step.is-done .ttw-step-name {
        color: var(--ttw-ink);
    }

    .ttw-step-value {
        overflow: hidden;
        font-size: 0.6875rem;
        line-height: 1.35;
        color: var(--ttw-ink-faint);
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
    }

    .ttw-step.is-done .ttw-step-value {
        color: var(--ttw-accent-ink);
    }

    /* ---------- Main column ---------- */

    .ttw-main {
        display: flex;
        flex-direction: column;
        min-width: 0;
        min-height: 0;
    }

    .ttw-scroll {
        flex: 1 1 auto;
        min-height: 0;
        padding: 1.75rem 2rem 1.5rem;
        overflow-y: auto;
    }

    .ttw-head {
        margin-bottom: 1.5rem;
        padding-right: 2rem;
    }

    .ttw-num {
        font-family: var(--ttw-mono);
        font-variant-numeric: tabular-nums;
        letter-spacing: 0;
        color: var(--ttw-accent);
    }

    .ttw-sep {
        color: var(--ttw-ink-faint);
    }

    .ttw-eyebrow-name {
        margin-left: 0.375rem;
        padding-left: 0.625rem;
        border-left: 1px solid var(--ttw-line);
        color: var(--ttw-ink-faint);
    }

    .ttw-title {
        margin: 0.625rem 0 0;
        font-size: 1.375rem;
        font-weight: 600;
        line-height: 1.3;
        letter-spacing: -0.015em;
        color: var(--ttw-ink);
    }

    .ttw-blurb {
        max-width: 56ch;
        margin: 0.5rem 0 0;
        font-size: 0.875rem;
        line-height: 1.55;
        color: var(--ttw-ink-soft);
    }

    /* ---------- Question blocks ---------- */

    .ttw-q + .ttw-q,
    .ttw-log {
        margin-top: 1.625rem;
    }

    .ttw-q-head {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-bottom: 0.625rem;
    }

    .ttw-q-label {
        flex: 0 0 auto;
        font-size: 0.6875rem;
        font-weight: 600;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--ttw-ink-faint);
    }

    .ttw-q-rule {
        flex: 1 1 auto;
        height: 1px;
        background-color: var(--ttw-line);
    }

    .ttw-q-text {
        margin: 0 0 0.875rem;
        font-size: 0.9375rem;
        font-weight: 500;
        line-height: 1.45;
        color: var(--ttw-ink);
    }

    /* ---------- Logging segmented controls ---------- */

    .ttw-log {
        max-width: 32rem;
    }

    .ttw-seg-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding: 0.625rem 0;

        & + .ttw-seg-row {
            border-top: 1px solid var(--ttw-line);
        }
    }

    .ttw-seg-label {
        font-size: 0.8125rem;
        font-weight: 600;
        color: var(--ttw-ink);
    }

    .ttw-seg {
        display: inline-flex;
        gap: 2px;
        padding: 2px;
        background-color: var(--ttw-track);
        border-radius: 0.4375rem;
    }

    .ttw-seg-btn {
        padding: 0.3125rem 0.6875rem;
        font-size: 0.75rem;
        font-weight: 600;
        color: var(--ttw-ink-soft);
        background: none;
        border: 0;
        border-radius: 0.3125rem;
        transition: color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;

        &:hover {
            color: var(--ttw-ink);
        }

        &.is-active {
            color: var(--ttw-accent-ink);
            background-color: var(--ttw-surface);
            box-shadow: 0 1px 2px rgba(18, 38, 63, 0.14);
        }
    }

    .ttw-mode {
        margin: 1rem 0 0;
        font-size: 0.75rem;
        color: var(--ttw-ink-faint);

        code {
            margin-left: 0.25rem;
            padding: 0.125rem 0.375rem;
            font-family: var(--ttw-mono);
            font-size: 0.75rem;
            color: var(--ttw-accent-ink);
            background-color: var(--ttw-accent-wash);
            border-radius: 0.25rem;
        }
    }

    /* ---------- Name step ---------- */

    .ttw-field {
        max-width: 28rem;

        & + .ttw-field,
        & + .ttw-publish {
            margin-top: 1.25rem;
        }
    }

    .ttw-label {
        display: block;
        margin-bottom: 0.375rem;
        font-size: 0.6875rem;
        font-weight: 600;
        letter-spacing: 0.07em;
        text-transform: uppercase;
        color: var(--ttw-ink-faint);
    }

    .ttw-help {
        margin: 0.375rem 0 0;
        font-size: 0.75rem;
        line-height: 1.45;
        color: var(--ttw-ink-faint);
    }

    .ttw-publish {
        max-width: 28rem;
        margin-top: 1.5rem;
        padding-top: 1.25rem;
        border-top: 1px solid var(--ttw-line);

        .form-check-label {
            font-size: 0.875rem;
            font-weight: 500;
            color: var(--ttw-ink);
        }
    }

    /* ---------- Footer ---------- */

    .ttw-foot {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        flex: 0 0 auto;
        padding: 0.875rem 2rem;
        background-color: var(--ttw-surface);
        border-top: 1px solid var(--ttw-line);
    }

    .ttw-foot-left,
    .ttw-foot-right {
        display: flex;
        align-items: center;
        gap: 0.75rem;
    }

    .ttw-btn {
        font-size: 0.8125rem;
        font-weight: 600;
        padding: 0.5rem 1.125rem;

        i {
            font-size: 0.875rem;
            vertical-align: -0.0625rem;
        }
    }

    .ttw-cancel {
        padding: 0.5rem 0.25rem;
        font-size: 0.8125rem;
        color: var(--ttw-ink-faint);
        background: none;
        border: 0;
        transition: color 0.15s ease;

        &:hover {
            color: var(--ttw-ink);
            text-decoration: underline;
        }
    }

    .ttw-hint {
        font-size: 0.75rem;
        color: var(--ttw-ink-faint);
    }

    /* ---------- Step transition ---------- */

    .ttw-pane-enter-active {
        transition: opacity 0.24s ease, transform 0.24s cubic-bezier(0.16, 0.84, 0.44, 1);
    }

    .ttw-pane-leave-active {
        transition: opacity 0.12s ease;
    }

    .ttw-pane-enter-from {
        opacity: 0;
        transform: translateY(8px);
    }

    .ttw-pane-leave-to {
        opacity: 0;
    }

    /* Roomier gutters and a slightly wider rail once there is desk to spare */
    @media (min-width: 1400px) {
        .ttw-grid {
            grid-template-columns: 17rem minmax(0, 1fr);
            height: min(82vh, 50rem);
        }

        .ttw-scroll {
            padding: 2rem 2.5rem 1.75rem;
        }

        .ttw-foot {
            padding-left: 2.5rem;
            padding-right: 2.5rem;
        }

        .ttw-rail {
            padding: 1.75rem 1.375rem;
        }

        .ttw-title {
            font-size: 1.5rem;
        }
    }

    @supports (height: 1dvh) {
        @media (min-width: 1400px) {
            .ttw-grid {
                height: min(82dvh, 50rem);
            }
        }
    }

    /* The rail collapses to a scrollable strip of steps above the content, and
       the content column keeps its own scroll so the footer stays reachable */
    @media (max-width: 767.98px) {
        .ttw-grid {
            grid-template-columns: minmax(0, 1fr);
            grid-template-rows: auto minmax(0, 1fr);
            height: min(88vh, 46rem);
        }

        .ttw-rail {
            border-right: 0;
            border-bottom: 1px solid var(--ttw-line);
        }

        .ttw-steps {
            display: flex;
            gap: 0.25rem;
            margin-top: 1rem;
            overflow-x: auto;
        }

        .ttw-step:not(:last-child)::before {
            display: none;
        }

        .ttw-step-value {
            display: none;
        }

        .ttw-scroll,
        .ttw-foot {
            padding-left: 1.25rem;
            padding-right: 1.25rem;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .ttw-rule-fill,
        .ttw-pane-enter-active,
        .ttw-pane-leave-active {
            transition: none;
        }
    }
}

body.dark-mode .ttw {
    --ttw-ink: #{$dark-text-primary};
    --ttw-ink-soft: #{$dark-text-secondary};
    --ttw-ink-faint: #{$dark-text-muted};
    --ttw-line: #{$dark-border-color};
    --ttw-surface: #{$dark-card-bg};
    --ttw-rail-top: #{$dark-sidebar-bg};
    --ttw-rail-bottom: #15181e;
    --ttw-accent: #4d90ea;
    --ttw-accent-ink: #a8caf8;
    --ttw-accent-wash: rgba(77, 144, 234, 0.16);
    --ttw-track: #{$dark-input-bg};

    .ttw-step:not(:last-child)::before {
        background-color: $dark-border-color;
    }

    .ttw-step-dot {
        background-color: $dark-input-bg;
    }

    .ttw-step.is-current .ttw-step-dot {
        color: #0e1117;
    }

    .ttw-seg-btn.is-active {
        background-color: $dark-elevated-bg;
        box-shadow: none;
    }
}
</style>
